// src/App.tsx
import React from 'react'
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom'
import { MyDiagramsPage } from './components/MyDiagrams'
import { PublicDiagramsPage } from './components/PublicDiagrams'
import { DiagramLoader } from './components/DiagramLoader'
import { NotFoundPage } from './components/NotFound'
import { NewDiagramRedirect } from './components/NewDiagramRedirect'
import { AuthProvider, useAuth } from './hooks/useAuth'
import { AuthCallbackPage } from './components/Auth/AuthCallbackPage'
import { ResetPasswordPage } from './components/Auth/ResetPasswordPage'
import { ModelProvider } from '@stores/modelStore'
import { SceneProvider } from '@stores/sceneStore'
import { UiStateProvider } from '@stores/uiStateStore'
import { AuthModalProvider } from '@/contexts/AuthModalContext'
import { LandingPage } from './components/LandingPage'
import { ToastViewport } from './components/Toast'

// Protected route wrapper
const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { user, loading } = useAuth()
  const location = useLocation()

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="text-gray-600">Loading...</div>
      </div>
    )
  }

  if (!user) {
    return <Navigate to="/" replace state={{ returnTo: `${location.pathname}${location.search}` }} />
  }

  return <>{children}</>
}

export const App: React.FC = () => {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AuthModalProvider>
        <Routes>
          {/* Landing page */}
          <Route path="/" element={<LandingPage />} />

          {/* Main editor - redirects to /diagram/:id with a new UUID */}
          <Route path="/editor" element={<NewDiagramRedirect />} />

          {/* Diagram viewer/editor - load specific diagram by ID */}
          <Route
            path="/diagram/:id"
            element={
              <ModelProvider>
                <SceneProvider>
                  <UiStateProvider>
                    <DiagramLoader />
                  </UiStateProvider>
                </SceneProvider>
              </ModelProvider>
            }
          />

          {/* Public diagrams page */}
          <Route path="/explore" element={<PublicDiagramsPage />} />
          <Route path="/auth/callback" element={<AuthCallbackPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />

          {/* Protected routes */}
          <Route
            path="/my-diagrams"
            element={
              <ProtectedRoute>
                <MyDiagramsPage />
              </ProtectedRoute>
            }
          />

          {/* 404 - Catch all unmatched routes */}
          <Route path="*" element={<NotFoundPage />} />
        </Routes>
        <ToastViewport />
        </AuthModalProvider>
      </AuthProvider>
    </BrowserRouter>
  )
}
